import { call, delay, fork, put, select } from 'redux-saga/effects';

import { Generator as ReduxGenerator } from 'types/Redux';

import * as FavoriteApi from 'api/favorites';
import * as ProgramItemsApi from 'api/programItems';

import * as calendarSagas from 'sagas/calendar';
import * as profileSagas from 'sagas/profile';
import * as programFeedViewSagas from 'sagas/programFeedView';
import * as programOverviewSagas from 'sagas/programOverview';

import { EventLevels, EventTypes } from 'defs/events';
import routes from 'defs/routes';
import Sequences from 'defs/sequences';

import { warn } from 'helper/debug';
import EventWrapper from 'models/global/EventWrapper';

import { actionCreators as connectionAC, getIsOnline } from 'store/connection';
import { actionCreators as eventsAC } from 'store/events';
import { EventType } from 'store/events';
import { actionCreators as optionalProgramItemsAC, OPTIONAL_ITEM_TYPES } from 'store/optionalProgramItems';
import { actionCreators as profileAC } from 'store/profile';
import { actionCreators as programDaysAC } from 'store/programDays';
import { getDayByDayNumber } from 'store/programDays';
import { getCurrentDayNumber } from 'store/programDays';
import { actionCreators as programItemsAC, getExternalId, getProgramId, mapToBackendType } from 'store/programItems';
import { actionCreators as sequenceAC, getSequenceIsRunning } from 'store/sequences';
import { getDayRef, getProgramItemRef } from 'store/transientPoolState';

export function* putCompleted(action: any): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	const currentDayNumber: number = yield select(getCurrentDayNumber);

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const completedItem = yield call(ProgramItemsApi.putItemCompleted, action.programItemId);
	const model = new EventWrapper(completedItem);

	yield put(programItemsAC.doCompleteSuccess(model.getData(), currentDayNumber));
	yield put(eventsAC.doAddEvents(model.getEvents()));
	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));
}

export function* putWorkoutCompleted(action: any): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	const currentDayNumber: number = yield select(getCurrentDayNumber);

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const completedItem = yield call(ProgramItemsApi.putWorkoutItemCompleted, action.programItemId, action.calories);
	const model = new EventWrapper(completedItem);

	yield put(programItemsAC.doCompleteSuccess(model.getData(), currentDayNumber));
	yield put(eventsAC.doAddEvents(model.getEvents()));
	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));
}

export function* postReplaceWorkoutWithCustomItem(action: any): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	const currentDayNumber: number = yield select(getCurrentDayNumber);

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const completedItem = yield call(
		ProgramItemsApi.postReplaceWorkoutWithCustomItem,
		action.programItemId,
		action.workoutExternalId,
		action.duration,
		action.met
	);
	const model = new EventWrapper(completedItem);

	yield put(programItemsAC.doCompleteSuccess(model.getData(), currentDayNumber));
	yield fork(programFeedViewSagas._loadProgramFeedView, {
		view: routes.today.indexRoute.id,
	});
	yield put(eventsAC.doAddEvents(model.getEvents()));

	const customMessageEvent: EventType = {
		eventLevel: EventLevels.NotAvailable,
		eventType: EventTypes.Message,
		code: 'custom-message',
		payload: {
			type: `swap.workout`,
		},
	} as any;

	yield delay(2000);

	yield put(eventsAC.doAddEvents([customMessageEvent]));

	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));
}

export function* putWorkoutCustomItemUpdate(action: any): ReduxGenerator {
	const { preventUpdate = false } = action;
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	const currentDayNumber: number = yield select(getCurrentDayNumber);

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const completedItem = yield call(
		ProgramItemsApi.putWorkoutCustomItemUpdate,
		action.programItemId,
		action.workoutExternalId,
		action.duration,
		action.met
	);

	const model = new EventWrapper({ data: completedItem } as any);

	yield put(programItemsAC.doCompleteSuccess(model.getData(), currentDayNumber));
	if (!preventUpdate) {
		yield fork(programFeedViewSagas._loadProgramFeedView, {
			view: routes.today.indexRoute.id,
		});
	}
	yield put(eventsAC.doAddEvents(model.getEvents()));
	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));
}

export function* putWorkoutCustomUpdateAndComplete(
	action: ReturnType<typeof programItemsAC.doWorkoutCustomUpdateAndComplete>
): ReduxGenerator {
	yield putWorkoutCustomItemUpdate({
		...action,
		preventUpdate: true,
	});
	yield putWorkoutCompleted({
		...action,
		calories: action.duration * action.met,
	});
}

export function* putClose(action: any): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	const currentDayNumber: number = yield select(getCurrentDayNumber);

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const closedItem = yield call(ProgramItemsApi.putItemClose, action.programItemId);
	const model = new EventWrapper(closedItem);

	yield put(programItemsAC.doCloseSuccess(model.getData(), currentDayNumber));
	yield put(eventsAC.doAddEvents(model.getEvents()));
	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));
}

export function* postTracking(action: any): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	const currentDayNumber: number = yield select(getCurrentDayNumber);

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const savedItem = yield call(ProgramItemsApi.postTracking, action.programItemId, {
		weight: action.measurement.weight,
		waist: action.measurement.waist,
		bodyFat: action.measurement.bodyFat,
	});
	const model = new EventWrapper(savedItem);

	yield put(programItemsAC.doSaveTrackingSuccess(model.getData(), currentDayNumber));
	yield put(eventsAC.doAddEvents(model.getEvents()));
	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));

	yield fork(profileSagas.getUserData, { force: true, preventUpdateLoadingState: true });
}

export function* putTracking(action: any): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const updatedItem = yield call(ProgramItemsApi.putTracking, action.programItemId, {
		weight: action.measurement.weight,
		waist: action.measurement.waist,
		bodyFat: action.measurement.bodyFat,
	});

	const model = new EventWrapper(updatedItem);

	yield put(programItemsAC.doUpdateTrackingSuccess(model.getData()));
	yield put(eventsAC.doAddEvents(model.getEvents()));
	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));

	yield fork(profileSagas.getUserData, { force: true, preventUpdateLoadingState: true });
	yield fork(programOverviewSagas.loadProgramOverview);
}

export function* putSlogan(action: any): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_CHECKING);
	if (isAlreadyLoading) {
		return;
	}

	const currentDayNumber: number = yield select(getCurrentDayNumber);

	yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_CHECKING));
	const savedItem = yield call(ProgramItemsApi.putSlogan, action.programItemId, action.slogan);
	const model = new EventWrapper(savedItem);

	yield put(programItemsAC.doSloganSuccess(model.getData(), currentDayNumber));
	yield put(profileAC.doSaveSloganSuccess(action.slogan));
	yield put(eventsAC.doAddEvents(model.getEvents()));
	yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_CHECKING));

	yield fork(profileSagas.getUserData);
}

function* addToFavorite(externalContentId: number, itemType: string, id: number): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	try {
		yield call(FavoriteApi.addFavorite, externalContentId, itemType);
		yield put(programItemsAC.doToggleFavoriteSuccess({ id, externalContentId, favorite: true, itemType }));
	} catch (e) {
		yield put(programItemsAC.doToggleFavoriteFailure(externalContentId));
	}
}

function* removeFromFavorite(externalContentId: number, itemType: string, id: number): ReduxGenerator {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	try {
		yield call(FavoriteApi.removeFavorite, externalContentId, itemType);
		yield put(programItemsAC.doToggleFavoriteSuccess({ id, externalContentId, favorite: false, itemType }));
	} catch (e) {
		yield put(programItemsAC.doToggleFavoriteFailure(externalContentId));
	}
}

export function* toggleFavorite(action: any): ReduxGenerator {
	let { externalId } = action;
	const { favorite, id, itemType } = action;
	const backendItemType: string | null = mapToBackendType(itemType);

	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	if (!externalId) {
		if (backendItemType === 'PRODUCT_DYNAMIC') {
			externalId = yield select(getProgramId, id);
		} else {
			externalId = yield select(getExternalId, id);
		}
	}

	if (!itemType || !backendItemType) {
		return warn(`toggleFavorite: Invalid type ${itemType} found.`);
	}

	if (!externalId) {
		return warn(`toggleFavorite: No externalId for ${itemType} #${id} found.`);
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.FAVORITE_IS_TOGGLING);
	if (isAlreadyLoading) {
		return warn(`toggleFavorite: Favorites are already loading, check for issue in timing!`);
	}

	yield put(sequenceAC.doStartSequence(Sequences.FAVORITE_IS_TOGGLING));

	if (favorite) {
		yield removeFromFavorite(externalId, backendItemType, id);
	} else {
		yield addToFavorite(externalId, backendItemType, id);
	}

	yield put(sequenceAC.doFinishSequence(Sequences.FAVORITE_IS_TOGGLING));
}

let isSwapping = false;
export function* swapProgramItem(action: ReturnType<typeof programItemsAC.doSwapItem>): Iterator<any> {
	const { contentType, externalId, lastDayRef } = action;

	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const programItemRef = yield select(getProgramItemRef);

	if (programItemRef) {
		if (isSwapping) {
			return;
		}

		isSwapping = true;
		const response: any = yield call(ProgramItemsApi.putSwapItem, programItemRef, contentType, externalId);
		const programItemModel = new EventWrapper(response);

		yield put(programItemsAC.doSwapItemSuccess(externalId, programItemModel.getData()));
		yield put(programItemsAC.doReplaceItem(programItemModel.getData()));
		isSwapping = false;

		yield fork(programFeedViewSagas._loadProgramFeedDay, {
			dayNumber: lastDayRef,
		});

		const customMessageEvent: EventType = {
			eventLevel: EventLevels.NotAvailable,
			eventType: EventTypes.Message,
			code: 'custom-message',
			payload: {
				type: `swap.${action.contentType}`,
			},
		} as any;
		yield delay(2000);
		yield put(eventsAC.doAddEvents([customMessageEvent]));
	} else {
		throw new Error(`No programItemRef given in transientPoolState (not coming from an item)`);
	}
}

export function* loadOptionalItem(action: ReturnType<typeof optionalProgramItemsAC.doLoadItem>): Iterator<any> {
	try {
		const contents = yield call(ProgramItemsApi.getOptionalContent, action.key);
		yield put(optionalProgramItemsAC.doLoadItemSuccess(action.key, contents));
	} catch (error) {
		yield put(optionalProgramItemsAC.doLoadItemFailure(error as any));
	}
}

export function* loadOptionalItems(): Iterator<any> {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkFetchRequestError());
	}

	yield fork(loadOptionalItem, optionalProgramItemsAC.doLoadItem(OPTIONAL_ITEM_TYPES.WORKOUT));
}

export function* createOptionalItem(action: ReturnType<typeof optionalProgramItemsAC.doCreateItem>): Iterator<any> {
	const { contentType, dayNumber, externalId, programItemId } = action;

	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	try {
		const response: any = yield call(
			ProgramItemsApi.postCreateFromOptional,
			externalId,
			contentType,
			programItemId,
			dayNumber
		);
		const programItemModel = new EventWrapper(response);
		const selectedUpdateDay = yield select(getDayRef);

		yield put(optionalProgramItemsAC.doCreateItemSuccess(programItemModel.getData()));
		yield fork(programFeedViewSagas._loadProgramFeedView, {
			view: routes.today.indexRoute.id,
		});
		yield fork(calendarSagas.updateCalendar, {
			currentDayNumber: selectedUpdateDay,
		});
	} catch (error) {
		yield put(optionalProgramItemsAC.doCreateItemFailure(error));
	}
}

export function* createCustomOptionalItem(action: ReturnType<typeof programItemsAC.doCreateCustomFromOptional>): any {
	const { contentType, exerciseExternalId, dayNumber, met, duration, programItemExternalId } = action;

	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	try {
		yield call(
			ProgramItemsApi.postCreateCustomFromOptional,
			contentType,
			programItemExternalId,
			dayNumber,
			exerciseExternalId,
			duration,
			met
		);

		yield fork(programFeedViewSagas._loadProgramFeedView, {
			view: routes.today.indexRoute.id,
		});

		const selectedUpdateDay = yield select(getDayRef);

		yield fork(calendarSagas.updateCalendar, {
			currentDayNumber: selectedUpdateDay,
		});

		const customMessageEvent: EventType = {
			eventLevel: EventLevels.NotAvailable,
			eventType: EventTypes.Message,
			code: 'custom-message',
			payload: {
				type: `swap.workout`,
			},
		} as any;
		yield call(delay, 1000);
		yield put(eventsAC.doAddEvents([customMessageEvent]));
	} catch (error) {
		// do nothing
	}
}

export function* postMoveItem(action: ReturnType<typeof programItemsAC.doMoveItem>): any {
	const { contentType, programItemExternalId, strategy, days } = action;
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CARD_IS_POSTPONING);
	if (isAlreadyLoading) {
		return;
	}

	try {
		const response = yield call(ProgramItemsApi.postMoveProgramItem, programItemExternalId, strategy, days);
		const model = new EventWrapper(response);

		const currentDayNumber = yield select(getCurrentDayNumber);
		const currentDay = yield select(getDayByDayNumber, currentDayNumber);

		yield put(sequenceAC.doStartSequence(Sequences.CARD_IS_POSTPONING));

		yield put(eventsAC.doAddEvents(model.getEvents()));

		yield put(programItemsAC.doMoveItemSuccess(programItemExternalId, strategy, currentDay.id, days));

		yield call(delay, 1500);

		const customMessageEvent: EventType = {
			eventLevel: EventLevels.NotAvailable,
			eventType: EventTypes.Message,
			code: 'custom-message',
			payload: {
				type: `postpone.${contentType}`,
			},
		} as any;
		yield put(eventsAC.doAddEvents([customMessageEvent]));

		yield put(
			programDaysAC.doMoveItem({
				programItemId: programItemExternalId,
				dayNumber: currentDayNumber,
				dayId: currentDay.id,
				days,
			})
		);

		yield fork(calendarSagas.updateCalendar, {
			currentDayNumber,
		});

		yield call(delay, 3000);

		yield put(sequenceAC.doFinishSequence(Sequences.CARD_IS_POSTPONING));
	} catch (error) {
		yield put(programItemsAC.doMoveItemFailure(programItemExternalId, strategy, days, error));
	}
}
